(() => {
  if (window.acquaCollectionUI) return;
  const mobile = window.matchMedia('(max-width: 859px)');
  const instances = new Map();
  const navigationInstances = new Map();
  const carousels = new Map();
  const sorts = new Map();
  const scrollTimers = new Map();
  const pilotSelector = '[data-acqua-collection-pilot]';
  let searchId = 0;
  const getInstance = section => instances.get(section?.querySelector(pilotSelector));
  const getNavigation = section => navigationInstances.get(section?.querySelector('[data-acqua-navigation-groups]'));
  const text = (outer, key) => outer.dataset[key] || '';

  function initNavigation(scope) {
    scope.querySelectorAll('[data-acqua-navigation-groups]').forEach(root => {
      if (navigationInstances.has(root)) return;
      const titles = root.querySelector('[data-acqua-navigation-titles]');
      const rail = root.querySelector('[data-acqua-continuous-rail]');
      const tail = root.querySelector('[data-acqua-navigation-tail]');
      if (!titles || !rail) return;
      if (tail) tail.hidden = true;
      const entries = [...rail.querySelectorAll('[data-acqua-navigation-group]')].map(group => ({
        group,
        button: group.querySelector('[data-acqua-navigation-toggle]'),
        first: group.querySelector('a.acqua-collection-link'),
        items: [...group.querySelectorAll('a.acqua-collection-link')]
      })).filter(({ group, button, first }) => group.dataset.key && button && first);
      if (!entries.length) return;
      const heading = document.createElement('div');
      heading.className = 'acqua-collection-heading';
      titles.before(heading);
      const jumps = document.createElement('div');
      jumps.className = 'acqua-collection-group-jumps';
      jumps.style.setProperty('--acqua-jump-columns', String(entries.length === 4 ? 2 : Math.min(3, entries.length)));
      heading.append(jumps, titles);
      entries.forEach(entry => {
        const jump = document.createElement('button');
        jump.type = 'button';
        jump.className = 'acqua-collection-group-jump';
        jump.textContent = entry.button.querySelector('.acqua-collection-group-name').textContent.trim();
        jump.setAttribute('aria-controls', entry.button.getAttribute('aria-controls') || rail.id);
        jumps.append(jump);
        entry.jump = jump;
      });
      const links = [...rail.querySelectorAll('a.acqua-collection-link')];
      const prev = root.querySelector('[data-acqua-navigation-prev]');
      const next = root.querySelector('[data-acqua-navigation-next]');
      const removers = [];
      const instance = {
        root, rail, titles, entries,
        active: entries.some(({ group }) => group.dataset.key === root.dataset.activeKey) ? root.dataset.activeKey : entries[0].group.dataset.key,
        initialized: false, destroyed: false,
        targetLeft: null, requestedGroup: null, pending: null, anchor: null, raf: 0, restoreRaf: 0, observer: null
      };
      const listen = (node, name, handler, options) => {
        node.addEventListener(name, handler, options);
        removers.push(() => node.removeEventListener(name, handler, options));
      };
      const offset = node => node.getBoundingClientRect().left - rail.getBoundingClientRect().left - rail.clientLeft + rail.scrollLeft;
      const maximum = () => Math.max(0, rail.scrollWidth - rail.clientWidth);
      const clamp = left => Math.max(0, Math.min(left, maximum()));
      const captureAnchor = () => {
        if (!rail.clientWidth) return;
        const scroll = rail.scrollLeft;
        const link = links.find(link => offset(link) + link.getBoundingClientRect().width > scroll + 1) || links[links.length - 1];
        instance.anchor = { link, relativeOffset: offset(link) - scroll };
      };
      const sync = () => {
        if (instance.destroyed || !rail.clientWidth) return;
        const scroll = rail.scrollLeft;
        const width = rail.clientWidth;
        const inset = parseFloat(getComputedStyle(rail).paddingLeft) || 0;
        const bounds = entries.map(({ first, items }) => ({
          start: offset(first),
          end: offset(items[items.length - 1]) + items[items.length - 1].getBoundingClientRect().width
        }));
        let active = bounds.findIndex(({ end }) => end > scroll + inset + 1);
        const max = maximum();
        // Several final groups can share one scroll stop. Reaching it must not skip
        // the first of those groups, and clicking a heading must keep that choice.
        if (max > 1 && scroll >= max - 1) {
          const firstClamped = bounds.findIndex(({ start }) => start - bounds[0].start >= max - 1);
          if (firstClamped !== -1) active = firstClamped;
        }
        if (instance.requestedGroup !== null) {
          const requested = instance.requestedGroup;
          const destination = clamp(bounds[requested].start - bounds[0].start);
          if (Math.abs(scroll - destination) < 1) active = requested;
        }
        if (active === -1) active = entries.length - 1;
        let focusHeading = null;
        const focused = document.activeElement;
        const sides = bounds.map(({ start, end }) => end - scroll <= inset ? 'previous'
          : start - scroll >= width - inset ? 'next' : '');
        entries.forEach(({ jump, button }, index) => {
          const position = index < active ? 'previous' : index > active ? 'next' : 'current';
          jump.dataset.position = position;
          button.dataset.position = position;
          button.setAttribute('aria-pressed', String(index === active));
          jump.hidden = false;
          jump.dataset.side = sides[index];
          jump.setAttribute('aria-pressed', String(index === active));
          jump.style.left = '';
          jump.style.right = '';
        });
        let leftDock = inset;
        let rightDock = width - inset;
        if (!mobile.matches) {
          const jumpWidths = entries.map(({ jump }) => jump.getBoundingClientRect().width);
          const labelWidths = entries.map(({ button }) => button.querySelector('.acqua-collection-group-name').scrollWidth);
          // Dock a partly exposed group too if its full heading cannot fit beside the shortcuts.
          for (let pass = 0; pass <= entries.length; pass++) {
            leftDock = inset;
            rightDock = width - inset;
            sides.forEach((side, index) => {
              if (side === 'previous') leftDock += jumpWidths[index] + 8;
              if (side === 'next') rightDock -= jumpWidths[index] + 8;
            });
            let changed = false;
            sides.forEach((side, index) => {
              if (side) return;
              const left = bounds[index].start - scroll;
              const right = bounds[index].end - scroll;
              if (Math.min(rightDock, right) - Math.max(leftDock, left) >= labelWidths[index] + 4) return;
              sides[index] = left < leftDock ? 'previous' : 'next';
              entries[index].jump.dataset.side = sides[index];
              jumpWidths[index] = entries[index].jump.getBoundingClientRect().width;
              changed = true;
            });
            if (!changed) break;
          }
          let previousEdge = inset;
          let nextEdge = inset;
          entries.forEach(({ jump, button }, index) => {
            jump.hidden = !sides[index];
            if (jump.hidden && focused === jump) focusHeading = button;
            if (sides[index] === 'previous') {
              jump.style.left = previousEdge + 'px';
              previousEdge += jumpWidths[index] + 8;
            }
          });
          entries.slice().reverse().forEach(({ jump }, reverseIndex) => {
            const index = entries.length - reverseIndex - 1;
            if (sides[index] !== 'next') return;
            jump.style.right = nextEdge + 'px';
            nextEdge += jumpWidths[index] + 8;
          });
        }
        entries.forEach(({ group, button }, index) => {
          const { start, end } = bounds[index];
          const selected = index === active;
          const left = start - scroll;
          const right = end - scroll;
          // Keep the heading pinned while card gaps pass the leading edge.
          const labelLeft = Math.max(leftDock, left);
          const label = button.querySelector('.acqua-collection-group-name');
          group.classList.toggle('is-active', selected);
          button.classList.toggle('is-active', selected);
          button.setAttribute('aria-pressed', String(selected));
          button.style.width = (end - start) + 'px';
          button.style.transform = 'translateX(' + left + 'px)';
          button.style.visibility = right > inset && left < width - inset ? 'visible' : 'hidden';
          button.tabIndex = !mobile.matches && !sides[index] ? 0 : -1;
          if (label) {
            label.style.visibility = !mobile.matches && sides[index] ? 'hidden' : '';
            label.style.transform = 'translateX(' + Math.max(0, labelLeft - left) + 'px)';
            label.style.maxWidth = (!mobile.matches && sides[index] ? 0 : Math.max(0, Math.min(rightDock, right) - labelLeft)) + 'px';
          }
        });
        const focusedEntry = entries.find(({ button }) => button === focused);
        if (focusedEntry && !focusedEntry.jump.hidden) focusHeading = focusedEntry.jump;
        if (!mobile.matches) titles.inert = false;
        if (focusHeading) focusHeading.focus({ preventScroll: true });
        titles.inert = mobile.matches;
        if (mobile.matches) titles.setAttribute('aria-hidden', 'true');
        else titles.removeAttribute('aria-hidden');
        if (prev) prev.hidden = scroll <= 1;
        if (next) next.hidden = scroll >= maximum() - 1;
        if (instance.targetLeft !== null && Math.abs(scroll - instance.targetLeft) < 1) instance.targetLeft = null;
        instance.active = entries[active].group.dataset.key;
        captureAnchor();
      };
      const scrollTo = (left, instant = false) => {
        instance.targetLeft = clamp(left);
        rail.scrollTo({ left: instance.targetLeft, behavior: instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        if (instant) sync();
      };
      const moveTo = index => {
        if (!rail.clientWidth) return;
        instance.requestedGroup = index;
        scrollTo(index === 0 ? 0 : offset(entries[index].first) - offset(links[0]));
        // A different heading may have the same destination and emit no scroll event.
        sync();
      };
      const layout = () => {
        if (instance.destroyed) return;
        if (!root.isConnected) return instance.destroy();
        if (!rail.clientWidth || !titles.clientWidth) return;
        const anchor = instance.anchor;
        const targetLeft = instance.targetLeft;
        titles.scrollLeft = 0;
        const railStyle = getComputedStyle(rail);
        if (instance.pending) {
          const saved = instance.pending;
          rail.scrollLeft = clamp(saved.scrollLeft);
          instance.initialized = true;
          if (saved.settled && (maximum() > 0 || saved.scrollLeft === 0)) instance.pending = null;
          sync();
        } else if (!instance.initialized) {
          instance.initialized = true;
          const current = rail.querySelector('a.acqua-collection-link[aria-current="page"]');
          const index = current ? entries.findIndex(({ group }) => group.contains(current)) : entries.findIndex(({ group }) => group.dataset.key === instance.active);
          const item = current || entries[Math.max(0, index)].first;
          let initialLeft = !current && index <= 0 ? 0 : offset(item) - offset(links[0]);
          if (current) {
            // Keep the current card clear of the overlaid next arrow.
            const right = offset(current) + current.getBoundingClientRect().width;
            const rightInset = Math.max(parseFloat(railStyle.paddingRight) || 0, next ? parseFloat(getComputedStyle(next).width) || 0 : 0);
            initialLeft = Math.max(initialLeft, right - rail.clientWidth + rightInset);
          }
          instance.requestedGroup = Math.max(0, index);
          rail.scrollLeft = clamp(initialLeft);
          sync();
        } else if (targetLeft !== null) {
          const destination = instance.requestedGroup === null
            ? targetLeft
            : offset(entries[instance.requestedGroup].first) - offset(links[0]);
          scrollTo(destination, true);
        } else {
          if (anchor?.link.isConnected) rail.scrollLeft = clamp(offset(anchor.link) - anchor.relativeOffset);
          sync();
        }
      };
      const scheduleSync = () => {
        if (!instance.pending && instance.requestedGroup !== null && instance.targetLeft === null) {
          const destination = clamp(offset(entries[instance.requestedGroup].first) - offset(links[0]));
          if (Math.abs(rail.scrollLeft - destination) >= 1) instance.requestedGroup = null;
        }
        cancelAnimationFrame(instance.raf);
        instance.raf = requestAnimationFrame(() => { instance.raf = 0; sync(); });
      };
      const cancelRestore = () => {
        cancelAnimationFrame(instance.restoreRaf);
        instance.restoreRaf = 0;
        instance.pending = null;
      };
      const cancelTarget = () => {
        cancelRestore();
        if (instance.targetLeft !== null) rail.scrollTo({ left: rail.scrollLeft, behavior: 'auto' });
        instance.targetLeft = null;
        instance.requestedGroup = null;
      };
      entries.forEach(({ group, button }) => {
        titles.append(button);
        group.hidden = false;
        button.removeAttribute('aria-expanded');
        group.classList.remove('is-open');
      });
      listen(root, 'click', event => {
        if (!(event.target instanceof Element)) return;
        const button = event.target.closest('button');
        const index = entries.findIndex(entry => entry.button === button || entry.jump === button);
        if (index !== -1) {
          event.preventDefault();
          cancelRestore();
          moveTo(index);
        } else if (button && (button === prev || button === next)) {
          event.preventDefault();
          cancelRestore();
          instance.requestedGroup = null;
          const left = instance.targetLeft === null ? rail.scrollLeft : instance.targetLeft;
          const spots = [...new Set([0, ...links.map(link => clamp(offset(link) - offset(links[0]))), maximum()])].sort((a, b) => a - b);
          const destination = button === prev
            ? spots.slice().reverse().find(spot => spot < left - 1) ?? 0
            : spots.find(spot => spot > left + 1) ?? maximum();
          scrollTo(destination);
        }
      });
      listen(jumps, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const available = entries.filter(({ jump }) => !jump.hidden);
        const index = available.findIndex(({ jump }) => jump === event.target);
        if (index < 0) return;
        event.preventDefault();
        const target = event.key === 'Home' ? 0 : event.key === 'End' ? available.length - 1
          : Math.max(0, Math.min(available.length - 1, index + (event.key === 'ArrowRight' ? 1 : -1)));
        available[target].jump.focus({ preventScroll: true });
      });
      let drag = null;
      let suppressClickUntil = 0;
      listen(rail, 'pointerdown', event => {
        suppressClickUntil = 0;
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        drag = { id: event.pointerId, x: event.clientX, left: rail.scrollLeft, moved: false };
      });
      listen(window, 'pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        const delta = event.clientX - drag.x;
        if (!drag.moved && Math.abs(delta) < 6) return;
        if (!drag.moved) {
          drag.moved = true;
          rail.classList.add('is-dragging');
          rail.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
        rail.scrollLeft = clamp(drag.left - delta);
      }, { passive: false });
      const finishDrag = event => {
        if (!drag || event.pointerId !== drag.id) return;
        if (drag.moved) suppressClickUntil = Date.now() + 400;
        if (rail.hasPointerCapture(drag.id)) rail.releasePointerCapture(drag.id);
        drag = null;
        rail.classList.remove('is-dragging');
      };
      listen(window, 'pointerup', finishDrag);
      listen(window, 'pointercancel', finishDrag);
      listen(rail, 'lostpointercapture', finishDrag);
      listen(rail, 'dragstart', event => event.preventDefault());
      listen(root, 'click', event => {
        if (Date.now() >= suppressClickUntil) return;
        suppressClickUntil = 0;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, true);
      listen(rail, 'scroll', scheduleSync, { passive: true });
      ['pointerdown', 'wheel', 'touchstart'].forEach(name => listen(rail, name, cancelTarget, { passive: true }));
      listen(rail, 'keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const index = links.indexOf(event.target.closest?.('a.acqua-collection-link'));
        if (index === -1) return;
        event.preventDefault();
        cancelTarget();
        const link = links[Math.max(0, Math.min(links.length - 1, index + (event.key === 'ArrowLeft' ? -1 : 1)))];
        link.focus({ preventScroll: true });
        const left = offset(link);
        const right = left + link.getBoundingClientRect().width;
        if (left < rail.scrollLeft) scrollTo(left, true);
        else if (right > rail.scrollLeft + rail.clientWidth) scrollTo(right - rail.clientWidth, true);
      });
      instance.update = sync;
      instance.layout = layout;
      instance.restore = state => {
        if (!Number.isFinite(state.scrollLeft)) return;
        const selected = entries.findIndex(({ group }) => group.dataset.key === state.active);
        if (selected !== -1) {
          instance.active = state.active;
          instance.requestedGroup = selected;
        }
        cancelAnimationFrame(instance.restoreRaf);
        instance.pending = { scrollLeft: state.scrollLeft, titleScrollLeft: Number.isFinite(state.titleScrollLeft) ? state.titleScrollLeft : 0, settled: false };
        // Native filtering restores in a microtask before section styles and ResizeObserver settle.
        // Keep the original position through that first layout, rather than saving its provisional clamp.
        layout();
        instance.restoreRaf = requestAnimationFrame(() => {
          instance.restoreRaf = requestAnimationFrame(() => {
            instance.restoreRaf = 0;
            if (instance.destroyed || !instance.pending) return;
            instance.pending.settled = true;
            layout();
          });
        });
      };
      instance.destroy = () => {
        if (instance.destroyed) return;
        instance.destroyed = true;
        instance.observer?.disconnect();
        cancelAnimationFrame(instance.raf);
        cancelAnimationFrame(instance.restoreRaf);
        removers.forEach(remove => remove());
        navigationInstances.delete(root);
      };
      navigationInstances.set(root, instance);
      instance.observer = new ResizeObserver(layout);
      [rail, titles, ...entries.map(({ group }) => group)].forEach(node => instance.observer.observe(node));
      layout();
      document.fonts?.ready.then(() => { if (!instance.destroyed) layout(); });
    });
  }

  function initSort(scope) {
    scope.querySelectorAll(`${pilotSelector} [data-acqua-sort]`).forEach(root => {
      if (sorts.has(root)) return;
      const select = root.querySelector('[data-productgrid-trigger-sort]');
      const control = root.querySelector('[data-acqua-sort-control]');
      const toggle = root.querySelector('[data-acqua-sort-toggle]');
      const panel = root.querySelector('[data-acqua-sort-options]');
      const label = root.querySelector('[data-acqua-sort-label]');
      const options = [...root.querySelectorAll('[data-acqua-sort-option]')];
      if (!select || !control || !toggle || !panel || !label || !options.length) return;
      const defaultLabel = label.textContent;
      const removers = [];
      const listen = (node, name, handler) => {
        node.addEventListener(name, handler);
        removers.push(() => node.removeEventListener(name, handler));
      };
      const sync = () => {
        options.forEach(option => option.setAttribute('aria-checked', String(option.value === select.value)));
        label.textContent = root.dataset.acquaSortExplicit === 'true' ? select.selectedOptions[0]?.textContent.trim() || defaultLabel : defaultLabel;
        toggle.setAttribute('aria-label', root.dataset.acquaSortExplicit === 'true' ? `${root.dataset.acquaSortTitle || defaultLabel}: ${label.textContent}` : defaultLabel);
      };
      const closeSort = (focus = false) => {
        panel.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        if (focus && toggle.isConnected) toggle.focus({ preventScroll: true });
      };
      const openSort = (last = false) => {
        const available = window.innerHeight - toggle.getBoundingClientRect().bottom - 16;
        panel.style.maxHeight = Math.max(176, Math.min(420, available)) + 'px';
        panel.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        const selected = options.find(option => option.value === select.value);
        (last ? options[options.length - 1] : selected || options[0]).focus({ preventScroll: true });
      };
      listen(toggle, 'click', () => panel.hidden ? openSort() : closeSort(true));
      listen(toggle, 'keydown', event => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        openSort(event.key === 'ArrowUp');
      });
      listen(panel, 'keydown', event => {
        const index = options.indexOf(document.activeElement);
        let next;
        if (event.key === 'ArrowDown') next = (index + 1) % options.length;
        else if (event.key === 'ArrowUp') next = (index - 1 + options.length) % options.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = options.length - 1;
        else if (event.key === 'Escape') {
          event.preventDefault(); event.stopPropagation(); closeSort(true); return;
        } else if (event.key === 'Tab') { closeSort(); return; }
        if (next !== undefined) { event.preventDefault(); options[next].focus({ preventScroll: true }); }
      });
      options.forEach(option => listen(option, 'click', () => {
        root.dataset.acquaSortExplicit = 'true';
        select.value = option.value;
        sync();
        closeSort(true);
        // Keep Empire's native handler as the only URL/filter writer.
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }));
      listen(select, 'change', sync);
      listen(document, 'click', event => { if (!root.contains(event.target)) closeSort(); });
      listen(root, 'focusout', event => { if (!root.contains(event.relatedTarget)) closeSort(); });
      sorts.set(root, { destroy() { removers.forEach(remove => remove()); sorts.delete(root); } });
      sync();
      control.hidden = false;
      root.classList.add('acqua-sort-ready');
    });
  }

  function cleanupDisconnected() {
    sorts.forEach((instance, root) => { if (!root.isConnected) instance.destroy(); });
    scrollTimers.forEach((timer, node) => { if (!node.isConnected) { clearTimeout(timer); scrollTimers.delete(node); } });
    instances.forEach((instance, outer) => {
      if (!outer.isConnected) { instance.observer?.disconnect(); instances.delete(outer); }
    });
    navigationInstances.forEach((instance, root) => {
      if (!root.isConnected) instance.destroy();
    });
    carousels.forEach((instance, carousel) => {
      if (!carousel.isConnected) { instance.observer.disconnect(); carousels.delete(carousel); }
    });
  }

  function updateSearch(instance) {
    const query = instance.searchInput.value.trim().toLocaleLowerCase();
    let matches = 0;
    instance.outer.classList.toggle('is-searching', Boolean(query));
    instance.groups.forEach(group => {
      const title = group.querySelector('.acqua-filter__title')?.textContent.trim().toLocaleLowerCase() || '';
      const titleMatches = Boolean(query && title.includes(query));
      const nativeInput = group.querySelector('[data-acqua-search-input]');
      if (nativeInput) {
        nativeInput.value = query && !titleMatches ? query : '';
        nativeInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      const values = [...group.querySelectorAll('[data-acqua-filter-value]')];
      let groupMatches = 0;
      values.forEach((value, index) => {
        if (!query) {
          if (!nativeInput) {
            const limit = Math.max(1, Number(group.dataset.valueLimit) || 6);
            const expanded = group.querySelector('[data-acqua-more]')?.getAttribute('aria-expanded') === 'true';
            value.hidden = !mobile.matches && !expanded && index >= limit && value.dataset.filterOpen !== 'true';
          }
          return;
        }
        const label = (value.dataset.valueLabel || value.querySelector('.acqua-filter__label')?.textContent || '').toLocaleLowerCase();
        const matchesValue = titleMatches || label.includes(query);
        value.hidden = !matchesValue;
        if (matchesValue) groupMatches += 1;
      });
      const matching = Boolean(query && (titleMatches || groupMatches));
      group.hidden = Boolean(query && !matching);
      group.classList.toggle('acqua-collection-search-match', matching);
      matches += titleMatches ? Math.max(1, groupMatches) : groupMatches;
    });
    instance.searchEmpty.hidden = !query || matches > 0;
  }

  function setSearchOpen(instance, open, focus = true) {
    instance.searchOpen = open;
    if (!open) instance.searchInput.value = '';
    refresh(instance);
    if (focus) (open ? instance.searchInput : instance.searchToggle).focus({ preventScroll: true });
  }

  function refresh(instance) {
    const { outer, source, filters, panel, groups, trigger, shortcuts } = instance;
    if (mobile.matches) {
      panel.querySelector('[data-acqua-collection-filter-mount]').append(filters);
      const heading = panel.querySelector('.acqua-collection-filter-heading');
      const closeButton = heading?.querySelector('[data-acqua-collection-close]');
      if (closeButton) closeButton.before(instance.searchToggle);
      else heading?.append(instance.searchToggle);
      if (heading) heading.after(instance.searchWrapper, instance.searchEmpty);
      else panel.prepend(instance.searchWrapper, instance.searchEmpty);
    } else {
      instance.placeholder.after(filters);
      const header = filters.querySelector('.acqua-filters__header');
      if (header) {
        header.append(instance.searchToggle);
        header.after(instance.searchWrapper, instance.searchEmpty);
      }
      else filters.prepend(instance.searchWrapper, instance.searchEmpty);
      instance.open = false;
    }
    panel.hidden = !mobile.matches || !instance.open;
    trigger.setAttribute('aria-expanded', String(!panel.hidden));
    groups.forEach(group => {
      const selected = group.dataset.acquaFilterKey === instance.active;
      group.classList.toggle('acqua-collection-active-group', selected);
      const button = instance.buttons.get(group.dataset.acquaFilterKey);
      button.setAttribute('aria-pressed', String(selected));
      if (selected) panel.querySelector('[data-acqua-collection-group-title]').textContent = group.querySelector('.acqua-filter__title')?.textContent || button.textContent;
    });
    instance.searchWrapper.hidden = !instance.searchOpen;
    instance.searchToggle.setAttribute('aria-expanded', String(instance.searchOpen));
    updateSearch(instance);
    shortcuts.querySelectorAll('button').forEach(button => {
      button.setAttribute('aria-expanded', String(!panel.hidden && button.dataset.acquaCollectionGroup === instance.active));
    });
    outer.classList.add('acqua-collection-ready');
  }

  function close(instance, focus = true) {
    instance.open = false;
    instance.searchOpen = false;
    instance.searchInput.value = '';
    refresh(instance);
    if (focus) (instance.returnFocus?.isConnected ? instance.returnFocus : instance.trigger)?.focus({ preventScroll: true });
  }

  function show(instance, key, trigger) {
    const active = instance.groups.some(group => group.dataset.acquaFilterKey === key) ? key : instance.active;
    if (instance.open && instance.returnFocus === trigger && active === instance.active) return close(instance);
    instance.active = active;
    instance.open = true;
    instance.returnFocus = trigger;
    refresh(instance);
  }

  function init(scope = document) {
    cleanupDisconnected();
    initNavigation(scope);
    initSort(scope);
    scope.querySelectorAll('.acqua-collection-browse a, .acqua-collection-breadcrumbs a, .acqua-collection-groups a').forEach(link => {
      const url = new URL(link.href, location.href);
      const current = new URL(location.href);
      ['preview_theme_id', '_fd', 'pb'].forEach(key => {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
      link.href = url.href;
    });
    scope.querySelectorAll('[data-acqua-collection-carousel]').forEach(carousel => {
      if (carousels.has(carousel) || carousel.closest('[data-acqua-continuous-rail]')) return;
      carousel.dataset.ready = 'true';
      const parent = carousel.closest('.acqua-collection-browse');
      if (!parent) return;
      const prev = parent.querySelector('[data-acqua-carousel-prev]');
      const next = parent.querySelector('[data-acqua-carousel-next]');
      if (!prev || !next) return;
      const update = () => {
        prev.hidden = carousel.scrollLeft <= 2;
        next.hidden = carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 2;
      };
      const state = { observer: null, update, aligned: false, pendingScrollLeft: null };
      const slide = direction => {
        state.aligned = true;
        carousel.scrollBy({ left: direction * carousel.clientWidth * .75, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      };
      prev.addEventListener('click', () => slide(-1));
      next.addEventListener('click', () => slide(1));
      carousel.addEventListener('scroll', () => { state.aligned = true; update(); }, { passive: true });
      const observer = new ResizeObserver(() => {
        if (carousel.clientWidth && state.pendingScrollLeft !== null) {
          carousel.scrollLeft = state.pendingScrollLeft;
          state.pendingScrollLeft = null;
          state.aligned = true;
        } else if (carousel.clientWidth && !state.aligned) {
          const current = carousel.querySelector('[aria-current="page"]');
          if (current) {
            const bounds = carousel.getBoundingClientRect();
            const item = current.getBoundingClientRect();
            if (item.left < bounds.left) carousel.scrollLeft += item.left - bounds.left;
            else if (item.right > bounds.right) carousel.scrollLeft += item.right - bounds.right;
          }
          state.aligned = true;
        }
        update();
      });
      observer.observe(carousel);
      update();
      state.observer = observer;
      carousels.set(carousel, state);
    });
    scope.querySelectorAll(pilotSelector).forEach(outer => {
      if (instances.has(outer)) return;
      const source = outer.querySelector('[data-productgrid-sidebar]');
      const filters = source?.querySelector('[data-acqua-filters]');
      const panel = outer.querySelector('[data-acqua-collection-panel]');
      const trigger = outer.querySelector('[data-productgrid-trigger-filters]');
      if (!source || !filters || !panel || !trigger) return;
      const groups = [...filters.querySelectorAll('[data-acqua-filter-key]')];
      if (!groups.length) return;
      const rail = panel.querySelector('[data-acqua-collection-groups]');
      const shortcuts = document.createElement('div');
      shortcuts.className = 'acqua-collection-shortcuts';
      trigger.closest('.utils-filter').insertAdjacentElement('afterend', shortcuts);
      trigger.setAttribute('aria-controls', panel.id);
      const count = filters.querySelector('.acqua-filters__count')?.firstChild?.textContent?.trim();
      trigger.querySelector('.utils-filter-text').textContent = text(outer, 'acquaAllFilters');
      trigger.setAttribute('aria-label', text(outer, 'acquaAllFilters') + (count ? ` (${count})` : ''));
      if (count) {
        const badge = document.createElement('span'); badge.className = 'acqua-collection-filter-count'; badge.textContent = count; badge.setAttribute('aria-hidden', 'true'); trigger.append(badge);
      }
      const placeholder = document.createElement('span'); placeholder.hidden = true; filters.before(placeholder);
      const instance = { outer, source, filters, placeholder, panel, groups, trigger, shortcuts, buttons: new Map(), active: groups[0].dataset.acquaFilterKey, open: false, searchOpen: false, returnFocus: trigger };
      const searchWrapper = document.createElement('div');
      searchWrapper.className = 'acqua-collection-filter-search';
      searchWrapper.id = `acqua-collection-filter-search-${++searchId}`;
      searchWrapper.hidden = true;
      const searchToggle = document.createElement('button');
      searchToggle.type = 'button';
      searchToggle.className = 'acqua-collection-search-toggle';
      searchToggle.setAttribute('aria-label', text(outer, 'acquaSearchFilters') || 'Search filters');
      searchToggle.setAttribute('aria-controls', searchWrapper.id);
      searchToggle.setAttribute('aria-expanded', 'false');
      searchToggle.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><circle cx="10.5" cy="10.5" r="6.5"></circle><path d="m15.5 15.5 5 5"></path></svg>';
      searchToggle.addEventListener('click', () => setSearchOpen(instance, !instance.searchOpen));
      const searchInput = document.createElement('input');
      searchInput.type = 'search';
      searchInput.dataset.acquaFilterSearch = '';
      searchInput.setAttribute('aria-label', text(outer, 'acquaSearchFilters') || 'Search filters');
      searchInput.placeholder = text(outer, 'acquaSearchFilters') || 'Search filters';
      searchInput.addEventListener('keydown', event => {
        if (event.key === 'Enter') event.preventDefault();
      });
      searchInput.addEventListener('input', () => updateSearch(instance));
      searchWrapper.append(searchInput);
      const searchEmpty = document.createElement('p');
      searchEmpty.className = 'acqua-collection-filter-empty';
      searchEmpty.textContent = text(outer, 'acquaNoFilterMatches') || 'No matching filters';
      searchEmpty.hidden = true;
      Object.assign(instance, { searchWrapper, searchInput, searchEmpty, searchToggle });
      groups.forEach(group => {
        const searchTitle = document.createElement('strong');
        searchTitle.className = 'acqua-collection-search-title';
        searchTitle.textContent = group.querySelector('.acqua-filter__title')?.textContent || '';
        group.prepend(searchTitle);
        const nativeSearch = group.querySelector('[data-acqua-value-search]');
        if (nativeSearch) nativeSearch.hidden = true;
      });
      groups.forEach(group => {
        const button = document.createElement('button'); button.type = 'button';
        button.dataset.acquaCollectionGroup = group.dataset.acquaFilterKey;
        button.textContent = group.querySelector('.acqua-filter__title')?.textContent || '';
        const count = group.querySelector('.acqua-filter__selected-count')?.firstChild?.textContent?.trim();
        if (count) { const badge = document.createElement('span'); badge.textContent = count; button.append(badge); }
        button.setAttribute('aria-controls', group.querySelector('[data-accordion-content]').id);
        rail.append(button); instance.buttons.set(group.dataset.acquaFilterKey, button);
      });
      const priorityKeys = text(outer, 'acquaPriorityKeys').split(',');
      priorityKeys.slice(0, 2).forEach(key => {
        const group = groups.find(group => group.dataset.acquaFilterKey.split('.').pop() === key);
        if (!group) return;
        const button = document.createElement('button'); button.type = 'button';
        button.dataset.acquaCollectionGroup = group.dataset.acquaFilterKey;
        const label = group.querySelector('.acqua-filter__title')?.textContent || '';
        button.textContent = label.replace(/\s*\(mm\)/, '');
        button.setAttribute('aria-controls', panel.id);
        if (group.querySelector('[data-filter-active]')) button.classList.add('is-selected');
        const chevron = document.createElement('span'); chevron.textContent = '⌄'; chevron.setAttribute('aria-hidden', 'true'); button.append(chevron);
        shortcuts.append(button);
      });
      instances.set(outer, instance); refresh(instance);
    });
  }

  document.addEventListener('scroll', event => {
    const node = event.target;
    if (!(node instanceof Element) || !node.closest(pilotSelector)) return;
    if (!node.matches('[data-acqua-continuous-rail], [data-productgrid-sidebar], .acqua-collection-filter-groups, .acqua-filter__body, .acqua-collection-shortcuts, .acqua-active-filters')) return;
    clearTimeout(scrollTimers.get(node));
    node.classList.add('is-scroll-active');
    scrollTimers.set(node, setTimeout(() => {
      node.classList.remove('is-scroll-active');
      scrollTimers.delete(node);
    }, 800));
  }, { capture: true, passive: true });

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const outer = event.target.closest(pilotSelector);
    const instance = instances.get(outer);
    const button = event.target.closest('[data-productgrid-trigger-filters], [data-acqua-collection-group], [data-acqua-collection-close]');
    if (!instance || !button || !mobile.matches) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (button.hasAttribute('data-acqua-collection-close')) return close(instance);
    if (button.closest('[data-acqua-collection-groups]')) {
      instance.active = button.dataset.acquaCollectionGroup; refresh(instance); return;
    }
    show(instance, button.dataset.acquaCollectionGroup || instance.active, button);
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const instance = instances.get(event.target.closest?.(pilotSelector));
    if (instance?.searchOpen) { event.preventDefault(); setSearchOpen(instance, false); }
    else if (instance?.open) { event.preventDefault(); close(instance); }
  });
  mobile.addEventListener('change', () => {
    instances.forEach(refresh);
    navigationInstances.forEach(instance => instance.layout());
  });
  document.addEventListener('shopify:section:unload', event => {
    sorts.forEach((instance, root) => { if (event.target.contains(root)) instance.destroy(); });
    scrollTimers.forEach((timer, node) => { if (event.target.contains(node)) { clearTimeout(timer); node.classList.remove('is-scroll-active'); scrollTimers.delete(node); } });
    carousels.forEach((instance, carousel) => {
      if (event.target.contains(carousel)) { instance.observer.disconnect(); carousels.delete(carousel); }
    });
    navigationInstances.forEach((instance, root) => {
      if (event.target.contains(root)) instance.destroy();
    });
    instances.forEach((instance, outer) => {
      if (event.target.contains(outer)) instances.delete(outer);
    });
  });
  window.acquaCollectionUI = {
    init,
    save(section) {
      const instance = getInstance(section);
      const navigation = getNavigation(section);
      if (!instance && !navigation) return null;
      const navigationState = navigation ? {
        active: (navigation.pending || navigation.targetLeft !== null) && navigation.requestedGroup !== null
          ? navigation.entries[navigation.requestedGroup].group.dataset.key
          : navigation.active,
        scrollLeft: navigation.pending?.scrollLeft ?? navigation.targetLeft ?? navigation.rail.scrollLeft,
        titleScrollLeft: navigation.pending?.titleScrollLeft ?? navigation.titles.scrollLeft
      } : null;
      if (!instance) return { top: window.scrollY, navigation: navigationState };
      const activeGroup = instance.groups.find(group => group.dataset.acquaFilterKey === instance.active);
      const focused = document.activeElement;
      return { open: instance.open, active: instance.active, searchOpen: instance.searchOpen, search: instance.searchInput.value, focusSearch: focused === instance.searchInput, top: window.scrollY, listTop: activeGroup?.querySelector('.acqua-filter__body')?.scrollTop || 0, railTop: instance.panel.querySelector('[data-acqua-collection-groups]').scrollTop, focusValue: focused?.dataset?.value || '', focusName: focused?.dataset?.name || '', navigation: navigationState };
    },
    restore(section, state) {
      const instance = getInstance(section);
      const navigation = getNavigation(section);
      if (!state) return;
      if (navigation && state.navigation) navigation.restore(state.navigation);
      if (!instance) return;
      instance.active = instance.groups.some(group => group.dataset.acquaFilterKey === state.active) ? state.active : instance.active;
      instance.open = Boolean(state.open && mobile.matches);
      instance.searchOpen = Boolean(state.searchOpen);
      instance.searchInput.value = instance.searchOpen ? state.search || '' : '';
      refresh(instance);
      const group = instance.groups.find(group => group.dataset.acquaFilterKey === instance.active);
      const body = group?.querySelector('.acqua-filter__body');
      if (body) body.scrollTop = state.listTop;
      instance.panel.querySelector('[data-acqua-collection-groups]').scrollTop = state.railTop;
      if (instance.open) {
        const focus = state.focusSearch && instance.searchOpen ? instance.searchInput : [...group.querySelectorAll('[data-filter-input]')].find(link => link.dataset.name === state.focusName && link.dataset.value === state.focusValue);
        (focus || instance.panel.querySelector('[data-acqua-collection-close]'))?.focus({ preventScroll: true });
      } else if (state.focusSearch && instance.searchOpen) {
        instance.searchInput.focus({ preventScroll: true });
      }
    }
  };
  document.addEventListener('shopify:section:load', event => init(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
